import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createOrderSource, orderSourcesQueryKeys } from '@api/orderSources';
import type { OrderSource } from '@api/orderSources';
import { getFieldErrors } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../useTranslation';

interface UseCreateOrderSourceOptions {
  onSuccess?: (orderSource: OrderSource) => void;
}

export function useCreateOrderSource(options?: UseCreateOrderSourceOptions) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: createOrderSource,
    onSuccess: (orderSource) => {
      queryClient.invalidateQueries({ queryKey: orderSourcesQueryKeys.lists() });
      options?.onSuccess?.(orderSource);
    },
    onError: (error) => {
      // Surface per-field reasons on 422 (e.g. "Name has already been taken").
      // ApiError.message is the server's generic i18n string ("Some fields are
      // not right") — useful as last resort, but unhelpful on its own.
      const fieldErrors = getFieldErrors(error);
      const description = fieldErrors
        ? Object.values(fieldErrors).join(' • ')
        : t('toast.create_error');
      toast({description, variant: 'destructive'});
    },
  });
}
